/**
 * The clients behind one figure on the book.
 *
 * A tile is a claim and the first thing anybody wants from a claim is the
 * list it was counted from. "1,027 unpaid" is a number to worry about; the
 * twenty whose trial ended longest ago and who still owe are something to do
 * about it.
 *
 * Each metric brings its own sentence saying what was counted and its own
 * order, because what makes a row interesting differs every time: for Unpaid
 * it is how long the money has been outstanding, for In trial how few days
 * are left, for the plan figures how much each one is worth.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { parseDate, daysAgo, daysTo } from '../../lib/dates.js';
import { TILE } from '../../domain/derive.js';
import { mrrOf } from '../../domain/pricing.js';
import { seenWithin } from '../../domain/clientReport.js';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import { Avatar, Pill, StatusPill, PayPill, EmptyState } from '../Primitives.jsx';

/*
 * What each tile counts, and how to read the list it opens.
 *
 * `pick` runs over every client with its derived record and is the same test
 * the tile uses (`ledgerStats`, `mrrSplit`), so the number on the tile and the
 * length of the list it opens cannot drift apart; `stat` is the one figure the
 * list is about, shown on the right of each row. `x.mrr` is the monthly rate
 * from `mrrOf`, worked out once per row below.
 */
/*
 * The windows the activity lists can be asked for.
 *
 * Here rather than on the tile strip: the strip is the page's headline
 * and always counts today, and this is where somebody is already
 * looking at the rows when they want to widen the question.
 */
/* Lengths, not distances back - Today is one day. See seenWithin. */
const WINDOWS = [
  [1, 'Today'],
  [7, '7 days'],
  [14, '14 days'],
  [30, '30 days'],
  [60, '60 days'],
  [90, '90 days']
];

/* What one account hands over on its own cycle — the figure both billing
   tiles are built from. */
const billing = x => Number(x.d.amt || x.r.amount || 0) || 0;

/*
 * Two of these lists mix cycles, so their column is the charge on a
 * monthly footing rather than the charge itself. `derive` carries no such
 * field - the code read `d.mrr` and got undefined, printing an em dash
 * against accounts that plainly pay - and the rate depends on settings the
 * component holds, so it arrives as a third argument beside the formatter.
 */

const METRICS = {
  total: {
    title: 'Every client', icon: 'users', tone: 'g-purple',
    note: 'The whole book, newest first.',
    pick: TILE.total,
    sort: (a, b) => (parseDate(b.r.regOn) || 0) - (parseDate(a.r.regOn) || 0),
    stat: x => ({ n: x.r.regOn ? `${daysAgo(parseDate(x.r.regOn))}d` : '—', l: 'on the books' })
  },
  live: {
    title: 'Active', icon: 'zap', tone: 'g-green',
    note: 'Paying, in date, and not written off.',
    pick: TILE.live,
    sort: (a, b, rate) => rate(b) - rate(a),
    stat: (x, money, rate) => ({ n: rate(x) ? money(rate(x)) : '—', l: 'a month' })
  },
  /*
   * Active as the team means it: somebody opened XYRA this week.
   *
   * The tile above used to count the status column, which says 1,298 of
   * 1,314 and never moves. This counts sign-ins, so the list is ordered by
   * how recently — the top of it is who is using the product right now, and
   * the bottom is who is about to stop.
   */
  /*
   * Both halves take the window off the tile that opened them, so the
   * heading, the count and the rows are the same question the strip was
   * asking. A popup with its own fixed seven days opened on a different
   * list from the figure that was clicked.
   */
  active: {
    title: days => (days > 1 ? `Active in the last ${days} days` : 'Active today'),
    icon: 'zap',
    tone: 'g-green',
    note: days => `Signed in ${days > 1 ? `within ${days} days` : 'today'}. Status says Active for nearly every account on the book; this says the product is actually being used.`,
    pick: (x, days) => !x.d.excluded && seenWithin(x.d, days),
    sort: (a, b) => (a.d.dAct ?? 1e9) - (b.d.dAct ?? 1e9),
    stat: x => (x.d.dAct === null
      ? { n: '—', l: 'never seen' }
      : x.d.dAct === 0 ? { n: 'today', l: 'last in' }
        : { n: `${x.d.dAct}d`, l: 'ago' })
  },
  /* The other half of the pair. Ordered by how long they have been gone,
     longest first: the top of this list is who has effectively left, and
     the accounts the app has never seen at all sit above even them. */
  inactive: {
    title: days => (days > 1 ? `Not active in the last ${days} days` : 'Not active today'),
    icon: 'eyeoff',
    tone: 'g-slate',
    note: days => `Nothing logged ${days > 1 ? `in ${days} days` : 'today'} — including the accounts the app has never seen at all.`,
    pick: (x, days) => !x.d.excluded && !seenWithin(x.d, days),
    sort: (a, b) => (b.d.dAct ?? 1e9) - (a.d.dAct ?? 1e9),
    stat: x => (x.d.dAct === null
      ? { n: 'never', l: 'signed in' }
      : { n: `${x.d.dAct}d`, l: 'ago' })
  },
  trial: {
    title: 'In trial', icon: 'clock', tone: 'g-amber',
    note: 'Still inside the trial window. The ones with least time left are first — those are the conversations worth having today.',
    pick: TILE.trial,
    sort: (a, b) => (a.d.dT ?? 999) - (b.d.dT ?? 999),
    stat: x => {
      const n = x.d.dT;
      return n === null || n === undefined
        ? { n: '—', l: 'no end date' }
        : n < 0 ? { n: `${Math.abs(n)}d`, l: 'over', bad: true } : { n: `${n}d`, l: 'left' };
    }
  },
  /*
   * Who has paid, newest payment first.
   *
   * Unpaid is kept below because the chase list is still worth having — it
   * is reachable from the Payment filter — but it is not what a dashboard
   * tile should lead with on a book that is 96% free accounts.
   */
  paid: {
    title: 'Payment done', icon: 'card', tone: 'g-green',
    note: 'Money recorded against them. Most recent payment first.',
    pick: TILE.paid,
    sort: (a, b) => (parseDate(b.r.lastPaid) || 0) - (parseDate(a.r.lastPaid) || 0),
    stat: (x, fmtMoney) => ({
      n: x.r.lastPaid ? `${daysAgo(parseDate(x.r.lastPaid))}d` : fmtMoney(x.d.amt || 0),
      l: x.r.lastPaid ? 'since last paid' : 'on the plan'
    })
  },
  unpaid: {
    title: 'Unpaid', icon: 'card', tone: 'g-orange',
    note: 'Nothing recorded against them yet. Longest outstanding first.',
    pick: TILE.unpaid,
    sort: (a, b) => (parseDate(a.r.regOn) || 0) - (parseDate(b.r.regOn) || 0),
    stat: x => ({ n: x.r.regOn ? `${daysAgo(parseDate(x.r.regOn))}d` : '—', l: 'since sign-up' })
  },
  renew: {
    title: 'Renewals', icon: 'refresh', tone: 'g-blue',
    note: 'Due to renew soon. Soonest first.',
    pick: TILE.renew,
    sort: (a, b) => (a.d.dueIn ?? 999) - (b.d.dueIn ?? 999),
    stat: x => {
      const n = x.d.dE;
      return n === null || n === undefined
        ? { n: '—', l: 'no date' }
        : n < 0 ? { n: `${Math.abs(n)}d`, l: 'expired', bad: true } : { n: `${n}d`, l: 'to go' };
    }
  },
  /*
   * The tile adds up only what it can - somebody paying, with an amount on
   * their record - so the heading here counts the same, or the two disagree
   * about one word. The rest of the cycle stays in the list behind the Not
   * paid filter, because "who else is on a monthly plan" is the next
   * question and this is the only place that answers it.
   */
  /*
   * The two billing tiles, and the rows they are actually made of.
   *
   * These used to pick on the cycle column alone, which is a different
   * set from the one the tile counts: the tile is money, so it counts
   * accounts that have paid and carry an amount. Annual read "6 clients"
   * on the card and opened on 461 — every account whose cycle happens to
   * say Annual, including the 456 that have never handed over anything —
   * with the money adding to nothing.
   *
   * So the test is mrrSplit's, said again: paid, worth something, and on
   * that billing basis. "Not monthly" rather than "annual" for the second
   * one, because a half-yearly account is billed on a long cycle too and
   * the tile has always counted it.
   */
  monthly: {
    title: 'Monthly plans', icon: 'refresh', tone: 'g-pink',
    note: 'Billed every month, and paying. What each one hands over, biggest first.',
    pick: x => !x.d.excluded && x.d.live && x.d.paid && billing(x) > 0
      && String(x.r.cycle || '').toLowerCase() === 'monthly',
    sort: (a, b) => billing(b) - billing(a),
    stat: (x, money) => ({ n: money(billing(x)), l: 'a month' })
  },
  /* The tile counts every cycle longer than a month here — quarterly and
     half-yearly as well as annual — so the list does too. */
  annual: {
    title: 'Annual plans', icon: 'cal', tone: 'g-purple',
    note: 'Billed on a long cycle, and paying. What each one hands over, biggest first.',
    pick: x => !x.d.excluded && x.d.live && x.d.paid && billing(x) > 0
      && String(x.r.cycle || '').toLowerCase() !== 'monthly',
    sort: (a, b) => billing(b) - billing(a),
    stat: (x, money) => ({ n: money(billing(x)), l: 'a year' })
  },
  conv: {
    title: 'Converted from trial', icon: 'chart', tone: 'g-cyan',
    note: 'Started on a trial and are now paying. The conversion rate is these against everyone who has ever been on one.',
    pick: TILE.conv,
    sort: (a, b, rate) => rate(b) - rate(a),
    stat: (x, money, rate) => ({ n: rate(x) ? money(rate(x)) : '—', l: 'a month' })
  }
};

/**
 * The promo codes inside a list, biggest first.
 *
 * Paid and Not paid answer "how many"; this answers "off the back of what".
 * Four hundred and twenty-five of the twelve hundred accounts carry a code
 * and three hundred and fifty-nine of them are unique — so a flat column of
 * codes says nothing, and the handful that were used more than once are the
 * only ones that are a channel rather than a one-off.
 *
 * Counted paid as well as total, because a code that brought in ninety
 * accounts and no money is a different fact from one that brought in three
 * who all pay, and the tile above cannot tell you which you are looking at.
 */
function promoGroups(list, isPaid) {
  const by = new Map();
  list.forEach(x => {
    const code = String(x.r.promo || '').trim().toUpperCase();
    if (!code) return;
    const hit = by.get(code) || { code, rows: [], paid: 0, value: 0 };
    hit.rows.push(x);
    if (isPaid(x)) { hit.paid += 1; hit.value += billing(x); }
    by.set(code, hit);
  });
  return [...by.values()].sort((a, b) =>
    b.paid - a.paid || b.rows.length - a.rows.length || a.code.localeCompare(b.code));
}

export default function ClientStatView({ metric = 'total', days: from = 1, onClose }) {
  const { S, rows, derive, fmtMoney, fmtDate, setSheet } = useApp();
  const m = METRICS[metric] || METRICS.total;

  /* Opens on whatever the tile was showing, and widens from there. Only
     the two activity lists have a window to change. */
  const [days, setDays] = useState(from);
  const timed = metric === 'active' || metric === 'inactive';

  /* A metric that cares about the window takes it; the rest ignore the
     second argument, so one call shape serves both. */
  const all = rows.map(r => ({ r, d: derive(r) }));
  const rate = x => mrrOf(S, x.r, x.d);
  const everyone = all.filter(x => m.pick(x, days)).sort((a, b) => m.sort(a, b, rate));
  const title = typeof m.title === 'function' ? m.title(days) : m.title;
  const note = typeof m.note === 'function' ? m.note(days) : m.note;

  /*
   * Paid and not, with the counts on the buttons.
   *
   * A list of everyone on a monthly plan is two lists: the ones the figure
   * above is made of, and the ones it is not. Which of those somebody wants
   * changes by the minute - "what are we billing" one moment, "who has not
   * paid" the next - so both are a click apart and neither is hidden.
   *
   * Paid is the same test the figures use: money has actually been recorded
   * against them, not that a plan has been chosen.
   */
  const [pay, setPay] = useState('all');
  const isPaid = x => !!x.d.paid && billing(x) > 0;

  const paidCount = everyone.filter(isPaid).length;
  const groups = promoGroups(everyone, isPaid);
  const promoCount = groups.reduce((t, g) => t + g.rows.length, 0);
  const list = pay === 'paid' ? everyone.filter(isPaid)
    : pay === 'unpaid' ? everyone.filter(x => !isPaid(x))
      : everyone;

  /* The total of whatever the right-hand column is counting, where adding
     it up means something - and only over what has actually been paid,
     since that is what the tile says. */
  /*
   * The two plan tiles are sums of the charge itself, which is what they
   * say on the strip: the monthly one is a monthly rate and the annual one
   * is what those accounts hand over in a year. The other two are monthly
   * rate across mixed cycles, so they go through mrrOf rather than adding
   * an annual invoice to a monthly one.
   */
  const money = ['monthly', 'annual', 'live', 'conv'].includes(metric)
    ? list.filter(isPaid)
      .reduce((t, x) => t + (metric === 'live' || metric === 'conv'
        ? mrrOf(S, x.r, x.d) : billing(x)), 0)
    : null;

  /*
   * One client, drawn the same way in the list and under a promo code.
   *
   * The promo tab used to show a truncated name on a grey chip and
   * nothing else - a code, a bar that is always full when there is one
   * code, and a word. Somebody reading "1 of 8 came in on a code" wants
   * to know who, on what plan, paying what; that is this row, so the
   * code gets the rows rather than a summary of them.
   */
  const row = ({ r, d }) => {
    const s = m.stat({ r, d }, fmtMoney, rate);
    return (
      <button
        type="button"
        className="psvrow"
        key={r.id}
        onClick={() => setSheet({ kind: 'clientView', id: r.id })}
      >
        <Avatar name={r.name} />

        <div className="psvwho">
          <b>{r.name || 'Untitled'}</b>
          <small>{r.email || r.contact || <span className="dim">no address on the record</span>}</small>
          <div className="psvmeta">
            {r.regOn ? <span><Icon name="cal" />joined {fmtDate(parseDate(r.regOn))}</span> : null}
            {r.source ? <span><Icon name="zap" />{r.source}</span> : null}
            {d.flags.length ? <span><Icon name="alert" />{d.flags[0].txt}</span> : null}
          </div>
        </div>

        <div className="psvtags">
          {r.plan ? <Pill tone="gr-violet">{r.plan}</Pill> : null}
          <StatusPill value={r.status} />
          <PayPill value={r.pay} />
        </div>

        <div className="psvown">
          {r.handler
            ? <Pill tone="gr-cyan" icon="user">{r.handler}</Pill>
            : <span className="psvnone">Unowned</span>}
          <small>{r.cycle || ''}</small>
        </div>

        <div className="psvstat">
          <b className={s.bad ? 'bad' : ''}>{s.n}</b>
          <small>{s.l}</small>
        </div>
      </button>
    );
  };

  return (
    <Sheet
      icon={m.icon}
      tone={m.tone}
      title={title}
      onClose={onClose}
      closeLabel="Close"
      wide
      bodyClass="psv"
      hero={(
        <div className={`psvhero ${m.tone}`}>
          <div>
            {/* The figure the tile shows, so the two never disagree. */}
            <b>{money !== null ? fmtMoney(money) : everyone.length}</b>
            <small>{note}</small>
          </div>
          <div className="psvchips">
            <span className="psvchip"><b>{everyone.length}</b> clients</span>
            {money !== null ? (
              <span className="psvchip"><b>{paidCount}</b> paying</span>
            ) : null}
          </div>
        </div>
      )}
    >
      {timed ? (
        <div className="psvwin" role="tablist" aria-label="Activity window">
          <span>Last seen within</span>
          {WINDOWS.map(([n, label]) => (
            <button
              key={n}
              type="button"
              role="tab"
              aria-selected={days === n}
              className={days === n ? 'on' : undefined}
              onClick={() => { setDays(n); setPay('all'); }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Both halves a click apart, with their sizes on the buttons: which
          one somebody wants changes between "what are we billing" and "who
          has not paid", often in the same minute. */}
      <div className="paytabs">
        {[
          ['all', 'Everyone', everyone.length],
          ['paid', 'Paid', paidCount],
          ['unpaid', 'Not paid', everyone.length - paidCount],
          /* Not a fourth slice of the same list — a different cut of it. It
             is here rather than on its own screen because "who paid" and
             "what brought them" are asked in the same breath. */
          ['promo', 'Promo code', promoCount]
        ].map(([k, label, n]) => (
          <button
            key={k}
            type="button"
            className={`paytab${pay === k ? ' on' : ''}${k === 'unpaid' && n ? ' warn' : ''}`}
            onClick={() => setPay(k)}
          >
            {label}<span>{n}</span>
          </button>
        ))}
      </div>
      {pay === 'promo' ? (
        <div className="psvpromo">
          {groups.length ? (
            <>
              <p className="psvpromo-head">
                <Icon name="tag" />
                {`${promoCount} of ${everyone.length} came in on a code · ${groups.length} ${groups.length === 1 ? 'code' : 'codes'}`}
              </p>
              {groups.map(g => (
                <section className="psvcode" key={g.code}>
                  <header>
                    <code>{g.code}</code>
                    <span className="psvcode-n">
                      <b>{g.rows.length}</b> {g.rows.length === 1 ? 'client' : 'clients'}
                    </span>
                    <em className={g.paid ? 'ok' : ''}>
                      {g.paid ? `${g.paid} paying` : 'none paying'}
                    </em>
                    {/* Only where the tile above is a money figure — on the
                        trial and renewal lists a total would be a guess. */}
                    {money !== null && g.value ? <b className="psvcode-m">{fmtMoney(g.value)}</b> : null}
                    {/* The share of the tab, and only worth drawing when
                        there is another code to compare against. */}
                    {groups.length > 1 ? (
                      <span className="psvpromo-bar" aria-hidden="true">
                        <i style={{ width: `${Math.round(g.rows.length / groups[0].rows.length * 100)}%` }} />
                      </span>
                    ) : null}
                  </header>
                  <div className="psvrows">{g.rows.map(row)}</div>
                </section>
              ))}
            </>
          ) : (
            <div style={{ gridColumn: '1/-1' }}>
              <EmptyState icon="tag" tone="g-slate" title="No promo codes here">
                Nobody in this list came in on one.
              </EmptyState>
            </div>
          )}
        </div>
      ) : null}

      {pay !== 'promo' && !list.length ? (
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon={m.icon} tone={m.tone} title="Nothing here">
            No client on the book answers to this right now.
          </EmptyState>
        </div>
      ) : null}

      <div className="psvrows">
        {(pay === 'promo' ? [] : list).map(row)}
      </div>

      {list.length && pay !== 'promo' ? (
        <p className="psvfoot">
          <Icon name="eye" />
          Open any row for the whole record — the plan, the payments, the
          timeline and everything logged against them.
        </p>
      ) : null}
    </Sheet>
  );
}
